import { useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'
import {
  getToolName,
  isTextUIPart,
  isToolUIPart,
  type ChatStatus,
  type DynamicToolUIPart,
  type ToolUIPart,
} from 'ai'
import {
  ASSISTANT_ACTIVITY_LABELS,
  ASSISTANT_LIMIT_CODES,
  ASSISTANT_PRESENTATION_TOOL_NAMES,
  isAssistantToolError,
  type AssistantChatErrorBody,
  type AssistantLimitScope,
  type AssistantToolName,
} from './contract'
import type { AssistantMessage } from './AssistantProvider'
import AssistantMarkdown from './AssistantMarkdown'
import SparkleIcon from './SparkleIcon'
import AssistantToolResult from './results/AssistantToolResult'
import AssistantApprovalPart, {
  hasApprovalCard,
  type AssistantApprovalDecision,
} from './approvals/AssistantApprovalPart'

const isKnownAction = (name: string): name is AssistantToolName =>
  name in ASSISTANT_ACTIVITY_LABELS

const isPresentationOnly = (name: string) =>
  (ASSISTANT_PRESENTATION_TOOL_NAMES as readonly string[]).includes(name)

/**
 * One plain-language line per action ("Searching the marketplace…"). The
 * text comes from the contract, never from the model, and never names the
 * underlying tool.
 */
const ActivityLine = ({ part }: { part: ToolUIPart | DynamicToolUIPart }) => {
  const name = getToolName(part)
  if (isPresentationOnly(name)) return null
  const labels = isKnownAction(name) ? ASSISTANT_ACTIVITY_LABELS[name] : null
  const failed =
    part.state === 'output-error' ||
    (part.state === 'output-available' && isAssistantToolError(part.output))
  const isDone = part.state === 'output-available' && !failed
  const isRunning =
    part.state === 'input-streaming' || part.state === 'input-available'

  let text: string
  if (part.state === 'approval-requested') text = 'Waiting for your OK'
  // A denial the user didn't make (the server refused) isn't a cancel.
  else if (part.state === 'output-denied')
    text = hasApprovalCard(part) ? 'Cancelled' : 'Not done'
  else if (failed) text = 'That didn’t work this time'
  else if (isDone) text = labels?.done ?? 'Done'
  else text = labels?.running ?? 'Working on it…'

  return (
    <Box
      data-testid="assistant-activity"
      sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}
    >
      {isRunning && <CircularProgress size={12} />}
      {isDone && <CheckIcon sx={{ fontSize: 14 }} color="success" />}
      <Typography variant="caption" color="text.secondary">
        {text}
      </Typography>
    </Box>
  )
}

interface MessageBubbleProps {
  message: AssistantMessage
  /** Quick replies are only offered on the newest message, when idle. */
  onReply?: (text: string) => void
  onApproval?: (decision: AssistantApprovalDecision) => void
  isBusy: boolean
}

const MessageBubble = ({
  message,
  onReply,
  onApproval,
  isBusy,
}: MessageBubbleProps) => {
  const isUser = message.role === 'user'
  const bubble = (
    <Box
      data-testid={`assistant-message-${message.role}`}
      sx={{
        alignSelf: isUser ? 'flex-end' : 'stretch',
        flex: isUser ? undefined : 1,
        minWidth: 0,
        maxWidth: isUser ? '85%' : '100%',
        px: isUser ? 1.75 : 0,
        py: isUser ? 1.25 : 0,
        borderRadius: 1,
        bgcolor: isUser ? 'primary.main' : 'transparent',
        color: isUser ? 'common.white' : 'text.primary',
        typography: 'body2',
        fontWeight: isUser ? 600 : undefined,
        overflowWrap: 'anywhere',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        '& p': { my: 0.5 },
        '& ul, & ol': { pl: 3, my: 0.5 },
      }}
    >
      {message.parts.map((part, index) => {
        const key = `${message.id}-${index}`
        if (isTextUIPart(part)) {
          return isUser ? (
            <Box key={key} sx={{ whiteSpace: 'pre-wrap' }}>
              {part.text}
            </Box>
          ) : (
            <AssistantMarkdown key={key} text={part.text} />
          )
        }
        if (isToolUIPart(part)) {
          return (
            <Box key={key}>
              <ActivityLine part={part} />
              {hasApprovalCard(part) && (
                <AssistantApprovalPart
                  part={part}
                  onDecide={onApproval}
                  disabled={isBusy}
                />
              )}
              {part.state === 'output-available' && (
                <AssistantToolResult
                  output={part.output}
                  onReply={onReply}
                  disabled={isBusy}
                />
              )}
            </Box>
          )
        }
        return null
      })}
    </Box>
  )
  if (isUser) return bubble
  // The assistant's side carries the small sparkle avatar from the design.
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
      <Box
        aria-hidden
        sx={{
          width: 26,
          height: 26,
          flexShrink: 0,
          mt: 0.25,
          display: 'grid',
          placeItems: 'center',
          borderRadius: '50%',
          bgcolor: 'primary.lightest',
          color: 'primary.main',
        }}
      >
        <SparkleIcon small sx={{ fontSize: 14 }} />
      </Box>
      {bubble}
    </Box>
  )
}

/** The chat route rejected the session (its body carries the code). */
const isSessionError = (error: Error) =>
  (error as { statusCode?: number }).statusCode === 401 ||
  (error.message ?? '').includes('UNAUTHORIZED')

/** The chat route's JSON error body, when the failure carries one. */
const readErrorBody = (
  error: Error
): Partial<AssistantChatErrorBody> | null => {
  const raw =
    (error as { responseBody?: unknown }).responseBody ?? error.message
  if (typeof raw !== 'string') return null
  try {
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object'
      ? (parsed as Partial<AssistantChatErrorBody>)
      : null
  } catch {
    return null
  }
}

/** A usage limit (429): retrying before it resets gets the same answer. */
const readLimitError = (error: Error) => {
  const body = readErrorBody(error)
  return body?.code &&
    (ASSISTANT_LIMIT_CODES as readonly string[]).includes(body.code)
    ? body
    : null
}

/** "3:00 PM EDT" in the user's own time zone. */
const formatResetTime = (resetAt: string | undefined) => {
  const at = resetAt ? new Date(resetAt) : null
  if (!at || Number.isNaN(at.getTime())) return null
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(at)
}

const LIMIT_COPY: Record<Exclude<AssistantLimitScope, 'user_hour'>, string> = {
  user_concurrent:
    'You already have an answer in progress. Try again when it finishes.',
  user_day: 'You’ve reached today’s assistant limit; it resets at midnight ET.',
  org_day:
    'Your firm has reached today’s assistant limit; it resets at midnight ET.',
  global_day:
    'Ask Attorney Share is paused for today; it resets at midnight ET.',
}

const describeLimit = (body: Partial<AssistantChatErrorBody>) => {
  const scope: AssistantLimitScope =
    body.scope ??
    (body.code === 'ASSISTANT_BUDGET_EXHAUSTED' ? 'org_day' : 'user_day')
  if (scope === 'user_hour') {
    const time = formatResetTime(body.resetAt)
    return time
      ? `You’ve reached this hour’s assistant limit. It resets at ${time}.`
      : 'You’ve reached this hour’s assistant limit. It resets at the top of the hour.'
  }
  return LIMIT_COPY[scope]
}

const isTooLongError = (error: Error) =>
  readErrorBody(error)?.code === 'CONVERSATION_TOO_LONG' ||
  (error.message ?? '').includes('CONVERSATION_TOO_LONG')

/**
 * Whether "Try again" can help: not for an ended session, a chat that is too
 * long, or a usage limit (except answers in progress, which clears quickly).
 */
const canRetry = (error: Error) => {
  if (isSessionError(error) || isTooLongError(error)) return false
  const limit = readLimitError(error)
  return !limit || limit.scope === 'user_concurrent'
}

/** Turns a failed request into words an attorney can act on. */
export const describeAssistantError = (error: Error) => {
  const limit = readLimitError(error)
  if (limit) return describeLimit(limit)
  if (isTooLongError(error)) {
    return 'This conversation is too long to continue. Select Clear to start a new one.'
  }
  if (isSessionError(error)) {
    return 'Your session has ended. Please sign in again to keep going.'
  }
  if ((error.message ?? '').includes('ASSISTANT_UNAVAILABLE')) {
    return 'Ask Attorney Share isn’t available right now. Please try again later.'
  }
  return 'Sorry, something went wrong. Please try again in a moment.'
}

interface AssistantMessagesProps {
  messages: AssistantMessage[]
  status: ChatStatus
  error: Error | undefined
  /** Re-sends the last question after a failure. */
  onRetry: () => void
  onReply: (text: string) => void
  /** Answers an Approve/Cancel card. */
  onApproval?: (decision: AssistantApprovalDecision) => void
}

const AssistantMessages = ({
  messages,
  status,
  error,
  onRetry,
  onReply,
  onApproval,
}: AssistantMessagesProps) => {
  const endRef = useRef<HTMLDivElement>(null)
  const isBusy = status === 'submitted' || status === 'streaming'

  // Keep the newest words in view while the answer streams in.
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' })
  }, [messages, status])

  return (
    <Box
      // The announcer speaks for the conversation; reading every streamed
      // chunk here would talk over it.
      role="log"
      aria-live="off"
      aria-label="Conversation"
      aria-busy={isBusy}
      tabIndex={0}
      sx={{
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
      }}
    >
      {messages.map((message, index) => (
        <MessageBubble
          key={message.id}
          message={message}
          onReply={index === messages.length - 1 ? onReply : undefined}
          onApproval={onApproval}
          isBusy={isBusy}
        />
      ))}
      {status === 'submitted' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Thinking…
          </Typography>
        </Box>
      )}
      {error && (
        <Box
          role="alert"
          data-testid="assistant-error"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Typography variant="body2" color="error" sx={{ flex: 1 }}>
            {describeAssistantError(error)}
          </Typography>
          {canRetry(error) && (
            <Button size="small" variant="outlined" onClick={onRetry}>
              Try again
            </Button>
          )}
        </Box>
      )}
      <div ref={endRef} />
    </Box>
  )
}

export default AssistantMessages
